import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useVolver } from '@/shared/hooks/useVolver';
import { mealsApi, productsApi, type LabelExtraction } from '@/shared/api/endpoints';
import { formatConfidence } from '@/shared/format/nutrition';
import { describeError, useAsync } from '@/shared/hooks/useAsync';
import { Banner, Button, Card, Field, Segmented, Spinner } from '@/shared/ui';
import { BackIcon } from '@/shared/ui/icons';

function initial(value: number | null): string {
  return value === null ? '' : value.toString();
}

/** Los seis campos del rótulo, en el orden en que figuran en el envase. */
const MACRO_RULE: Record<string, string> = {
  proteinG: 'var(--protein)',
  carbohydratesG: 'var(--carbs)',
  fatG: 'var(--fat)',
};

/**
 * S-05 · la validación del rótulo.
 *
 * Es un formulario que tiene que sentirse como una confirmación rápida, no como una carga: lo que
 * el extractor leyó viene precargado y el camino corto es no tocar nada. Todo campo es editable
 * igual, porque el rótulo es la fuente y la foto es apenas un intento de leerlo.
 */
export function LabelPage() {
  const { mealId = '', itemId = '' } = useParams();
  const navigate = useNavigate();
  const volver = useVolver(`/comidas/${mealId}/items/${itemId}`);

  const extraction = useAsync<LabelExtraction>(
    () => mealsApi.labelExtraction(mealId, itemId),
    [mealId, itemId],
  );

  if (extraction.loading && extraction.data === null) {
    return <Spinner label="Leyendo el rótulo" />;
  }

  return (
    <>
      <header className="row-between">
        <button
          type="button"
          className="btn-square"
          aria-label="Volver"
          onClick={volver}
        >
          <BackIcon size={18} />
        </button>
        <h1 style={{ fontSize: 'var(--text-h2)' }}>Valores del rótulo</h1>
        <span style={{ width: 38 }} />
      </header>

      {extraction.error !== null && <Banner tone="danger">{extraction.error}</Banner>}

      <LabelForm
        mealId={mealId}
        itemId={itemId}
        extraction={extraction.data}
        onSaved={() => navigate(`/comidas/${mealId}/items/${itemId}`, { replace: true })}
      />
    </>
  );
}

function LabelForm({
  mealId,
  itemId,
  extraction,
  onSaved,
}: {
  mealId: string;
  itemId: string;
  extraction: LabelExtraction | null;
  onSaved: () => void;
}) {
  const [kcal, setKcal] = useState(initial(extraction?.kcal ?? null));
  const [proteinG, setProteinG] = useState(initial(extraction?.proteinG ?? null));
  const [carbohydratesG, setCarbohydratesG] = useState(initial(extraction?.carbohydratesG ?? null));
  const [fatG, setFatG] = useState(initial(extraction?.fatG ?? null));
  const [fiberG, setFiberG] = useState(initial(extraction?.fiberG ?? null));
  const [sodiumMg, setSodiumMg] = useState(initial(extraction?.sodiumMg ?? null));
  const [perServing, setPerServing] = useState(extraction?.perServing ?? false);
  const [servingWeightG, setServingWeightG] = useState(initial(extraction?.servingWeightG ?? null));
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /*
    RF-52 · el aporte al catálogo.

    Prendido por omisión y con el nombre vacío: la casilla anuncia la intención, pero sin nombre no
    se publica nada. Así el caso corriente —alguien registrando su comida y nada más— no cambia,
    y el que quiere aportar sólo tiene que escribir cómo se llama.

    El nombre lo escribe la persona porque el OCR lee la tabla nutricional, no el frente del envase.
  */
  const [publishToCatalog, setPublishToCatalog] = useState(true);
  const [productName, setProductName] = useState('');
  const [productBrand, setProductBrand] = useState('');
  const [published, setPublished] = useState<string | null>(null);
  const [publishRejected, setPublishRejected] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);

  const complete =
    kcal !== '' &&
    proteinG !== '' &&
    carbohydratesG !== '' &&
    fatG !== '' &&
    (!perServing || servingWeightG !== '');

  const fields = [
    { key: 'kcal', label: 'Calorías', unit: 'kcal', value: kcal, set: setKcal, secondary: false },
    {
      key: 'proteinG',
      label: 'Proteína',
      unit: 'g',
      value: proteinG,
      set: setProteinG,
      secondary: false,
    },
    {
      key: 'carbohydratesG',
      label: 'Carbohidratos',
      unit: 'g',
      value: carbohydratesG,
      set: setCarbohydratesG,
      secondary: false,
    },
    { key: 'fatG', label: 'Grasas', unit: 'g', value: fatG, set: setFatG, secondary: false },
    { key: 'fiberG', label: 'Fibra', unit: 'g', value: fiberG, set: setFiberG, secondary: true },
    {
      key: 'sodiumMg',
      label: 'Sodio',
      unit: 'mg',
      value: sodiumMg,
      set: setSodiumMg,
      secondary: true,
    },
  ] as const;

  const submit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const aporta = publishToCatalog && productName.trim() !== '';

      const result = await mealsApi.submitLabel(mealId, itemId, {
        kcal: Number(kcal),
        proteinG: Number(proteinG),
        carbohydratesG: Number(carbohydratesG),
        fatG: Number(fatG),
        fiberG: fiberG === '' ? null : Number(fiberG),
        sodiumMg: sodiumMg === '' ? null : Number(sodiumMg),
        perServing,
        servingWeightG: servingWeightG === '' ? null : Number(servingWeightG),
        wasCorrected: touched,
        publishToCatalog: aporta,
        productName: aporta ? productName.trim() : null,
        productBrand: aporta && productBrand.trim() !== '' ? productBrand.trim() : null,
      });

      /*
        Si hubo aporte, la pantalla se queda: hay algo que decir y algo que deshacer. Sin aporte
        sigue de largo como siempre, porque no hay nada nuevo que contar.
      */
      if (!aporta) {
        onSaved();

        return;
      }

      setPublished(result.publishedProductId);
      setPublishRejected(result.publishRejected);
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setSubmitting(false);
    }
  };

  const readFields = [
    extraction?.kcal,
    extraction?.proteinG,
    extraction?.carbohydratesG,
    extraction?.fatG,
    extraction?.fiberG,
    extraction?.sodiumMg,
  ].filter((value) => value !== null && value !== undefined).length;

  const retirar = async () => {
    if (published === null) {
      return;
    }

    setWithdrawing(true);
    setError(null);

    try {
      await productsApi.withdraw(published);
      setPublished(null);
      setPublishRejected('Lo retiraste. Ya no aparece en el catálogo.');
    } catch (cause) {
      setError(describeError(cause));
    } finally {
      setWithdrawing(false);
    }
  };

  /*
    La comida ya quedó guardada. Lo único que falta decir es qué pasó con el aporte, y ofrecer
    deshacerlo: publicar en silencio dejaría a la persona sin saber que su lectura quedó visible
    para todos y sin forma de sacarla.
  */
  if (published !== null || publishRejected !== null) {
    return (
      <>
        <Banner tone={published !== null ? 'info' : 'conf'}>
          {published !== null
            ? `«${productName.trim()}» se sumó al catálogo. Cualquiera puede encontrarlo ahora.`
            : publishRejected}
        </Banner>

        {error !== null && <Banner tone="danger">{error}</Banner>}

        <div className="sticky-foot">
          {published !== null && (
            <Button variant="quiet" block onClick={() => void retirar()} disabled={withdrawing}>
              {withdrawing ? 'Retirando…' : 'Retirarlo del catálogo'}
            </Button>
          )}
          <Button variant="primary" block onClick={onSaved}>
            Listo
          </Button>
          <p className="micro muted">
            Tu comida ya quedó registrada. Esto es sólo sobre el catálogo compartido.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <Card size="tight">
        {extraction === null || !extraction.extractionAvailable ? (
          <span className="small">
            No hay lectura automática de rótulos. Cargá los valores como figuran en el envase.
          </span>
        ) : (
          <>
            <strong className="small">Se leyeron {readFields} de 6 campos</strong>
            <span className="micro muted">
              Base: {extraction.perServing ? 'por porción' : 'por 100 g'}
              {' · confianza del OCR '}
              {formatConfidence(0.9)}
            </span>
          </>
        )}
      </Card>

      {extraction !== null && extraction.extractionAvailable && !extraction.hasMandatoryFields && (
        <Banner tone="conf">
          No se pudieron leer todos los valores obligatorios. Completá los que faltan.
        </Banner>
      )}

      {error !== null && <Banner tone="danger">{error}</Banner>}

      <Segmented
        label="Base de los valores"
        value={perServing ? 'serving' : 'hundred'}
        onChange={(value) => {
          setPerServing(value === 'serving');
          setTouched(true);
        }}
        options={[
          { value: 'hundred', label: 'Por 100 g' },
          { value: 'serving', label: 'Por porción' },
        ]}
      />

      <Card size="flush">
        {fields.map((field) => (
          <label key={field.key} className="option-row" style={{ cursor: 'text' }}>
            <span
              className="macro-rule"
              style={{
                width: 3,
                height: 22,
                background: MACRO_RULE[field.key] ?? 'var(--rule)',
                flex: 'none',
              }}
            />
            <span
              className={field.secondary ? 'option-title muted' : 'option-title'}
              style={{ flex: 1 }}
            >
              {field.label}
            </span>
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step="any"
              value={field.value}
              onChange={(event) => {
                field.set(event.target.value);
                setTouched(true);
              }}
              style={{
                width: 96,
                border: 'none',
                background: 'transparent',
                textAlign: 'right',
                font: 'inherit',
                fontSize: '0.9375rem',
                fontWeight: 800,
                fontVariantNumeric: 'tabular-nums',
                color: 'inherit',
              }}
            />
            <span className="micro muted" style={{ width: 28 }}>
              {field.unit}
            </span>
          </label>
        ))}
      </Card>

      <p className="micro muted">
        Fibra y sodio se registran pero no son macros: no entran en el cálculo de calorías.
      </p>

      {perServing && (
        <div className="field">
          <label htmlFor="serving-weight">Peso de la porción (g)</label>
          <input
            id="serving-weight"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={servingWeightG}
            onChange={(event) => {
              setServingWeightG(event.target.value);
              setTouched(true);
            }}
          />
          <span className="micro muted">Sin esto no se puede llevar los valores a 100 g.</span>
        </div>
      )}

      {/*
        RF-52 · el aporte al catálogo compartido.

        Va al pie y no arriba porque no es lo que la persona vino a hacer: vino a registrar una
        comida. Y la casilla viene prendida con el nombre vacío a propósito — anuncia que existe la
        posibilidad sin publicar nada por accidente, porque sin nombre no se publica.
      */}
      <Card size="tight">
        <label className="option-row" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={publishToCatalog}
            onChange={(event) => setPublishToCatalog(event.target.checked)}
          />
          <span className="item-body">
            <span className="small">Sumarlo al catálogo para todos</span>
            <span className="micro muted">
              Cualquiera va a poder encontrarlo. Podés retirarlo después.
            </span>
          </span>
        </label>

        {publishToCatalog && (
          <>
            <Field
              label="Nombre del producto"
              hint="Como figura en el envase. Sin esto no se publica nada."
              value={productName}
              onChange={(event) => setProductName(event.target.value)}
              placeholder="Galletitas de agua"
            />
            <Field
              label="Marca (opcional)"
              value={productBrand}
              onChange={(event) => setProductBrand(event.target.value)}
              placeholder="Criollitas"
            />
          </>
        )}
      </Card>

      <div className="sticky-foot">
        <Button
          variant="primary"
          block
          onClick={() => void submit()}
          disabled={submitting || !complete}
        >
          {submitting ? 'Guardando…' : 'Está bien así'}
        </Button>
        {/*
          CU-04 paso 6 · el precio de tocar un campo. Decirlo antes evita que la confianza cambie
          por debajo sin que nadie entienda por qué.
        */}
        <p className="micro muted">
          {touched
            ? 'Como corregiste un campo, la estimación queda con confianza 1,00 · editado por vos.'
            : 'Si no tocás nada queda con confianza 0,90 · rótulo. Si corregís un campo, pasa a 1,00.'}
        </p>
      </div>
    </>
  );
}
